/**
 * IntegrationsPage — "Integraciones" (solo administradores).
 *
 * Una sola pantalla que centraliza las conexiones con APIs externas:
 *   - Meta (Marketing + Graph)           → /api/meta/*
 *   - Google Analytics (GA4 Data API)    → /api/integrations/google_analytics/*
 *   - Google Ads (OAuth + developer tok) → /api/integrations/google_ads/*
 *
 * Cada proveedor se pinta con la tarjeta genérica IntegrationCard. Los secretos
 * viajan una sola vez y se guardan cifrados; nunca vuelven al frontend.
 */
import React from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { FaFacebook, FaGoogle, FaChartLine, FaBullhorn, FaPlug, FaTiktok } from 'react-icons/fa';
import { metaApi } from '../../services/metaService';
import MetaExtraCredentials from '../MetaConfigPage/MetaExtraCredentials';
import { integrationsApi } from '../../services/integrationsService';
import IntegrationCard from './IntegrationCard';
import './IntegrationsPage.css';

// ── Adaptador de Meta a la forma normalizada que espera IntegrationCard ──────
const adaptMeta = (s) => ({
  configured: s.configured,
  status: s.status,
  values: { ad_account_id: s.ad_account_id, page_id: s.page_id, ig_user_id: s.ig_user_id },
  secretsConfigured: { access_token: !!s.configured },
  last_verified_at: s.last_verified_at,
  last_error: s.last_error,
  details: s.details,
});

const metaCardApi = {
  load: (u) => metaApi.getConfig(u).then(adaptMeta),
  save: (u, v) => metaApi.saveConfig(u, {
    accessToken: v.access_token, adAccountId: v.ad_account_id, pageId: v.page_id, igUserId: v.ig_user_id,
  }).then(adaptMeta),
  test: (u) => metaApi.testConfig(u).then(adaptMeta),
  remove: (u) => metaApi.deleteConfig(u),
};

const googleApi = (provider) => ({
  load: (u) => integrationsApi.getConfig(provider, u),
  save: (u, v) => integrationsApi.saveConfig(provider, u, v),
  test: (u) => integrationsApi.testConfig(provider, u),
  remove: (u) => integrationsApi.deleteConfig(provider, u),
});

const META_FIELDS = [
  { key: 'access_token', label: 'Token de System User', type: 'password', secret: true, placeholder: 'Pega el token de System User' },
  { key: 'ad_account_id', label: 'Ad Account ID', type: 'text', placeholder: 'act_1234567890', hint: 'Campañas pagadas (Marketing API).' },
  { key: 'page_id', label: 'Page ID', type: 'text', placeholder: '1234567890', hint: 'Métricas orgánicas de la Página.' },
  { key: 'ig_user_id', label: 'Instagram Business ID', type: 'text', placeholder: '17841400000000000', hint: 'Insights de Instagram Business.' },
];

const GA_FIELDS = [
  { key: 'property_id', label: 'Property ID (GA4)', type: 'text', placeholder: '123456789', hint: 'Numérico. Admin → Configuración de la propiedad (no es el tag G-XXXX).' },
  { key: 'service_account_json', label: 'Service Account (JSON)', type: 'textarea', secret: true, placeholder: 'Pega aquí el contenido completo del archivo .json del service account', hint: 'Dale a este service account acceso de Lector en GA4.' },
];

const GADS_FIELDS = [
  { key: 'customer_id', label: 'Customer ID', type: 'text', placeholder: '1234567890', hint: 'Sin guiones.' },
  { key: 'login_customer_id', label: 'Login Customer ID (manager, opcional)', type: 'text', placeholder: '1234567890' },
  { key: 'client_id', label: 'OAuth Client ID', type: 'text', placeholder: '...apps.googleusercontent.com' },
  { key: 'client_secret', label: 'OAuth Client Secret', type: 'password', secret: true },
  { key: 'refresh_token', label: 'Refresh Token', type: 'password', secret: true },
  { key: 'developer_token', label: 'Developer Token', type: 'password', secret: true, hint: 'Se solicita en tu cuenta de Google Ads Manager.' },
];

const GA_HELP = (
  <ol>
    <li>En Google Cloud: crea un proyecto y activa la <strong>Analytics Data API</strong>.</li>
    <li>Crea una <strong>Service Account</strong> y descarga su archivo <strong>JSON</strong>.</li>
    <li>En GA4 → Administrar → Acceso a la propiedad, da <strong>Lector</strong> al email del service account.</li>
    <li>Pega el JSON y el <strong>Property ID</strong> numérico, y pulsa Probar conexión.</li>
  </ol>
);

const GADS_HELP = (
  <ol>
    <li>Solicita un <strong>Developer Token</strong> en tu cuenta de Google Ads Manager (MCC).</li>
    <li>Crea credenciales <strong>OAuth Client</strong> (ID + secret) en Google Cloud.</li>
    <li>Genera un <strong>Refresh Token</strong> autorizando el scope de Google Ads.</li>
    <li>Pon el <strong>Customer ID</strong> (sin guiones) y, si usas MCC, el Login Customer ID.</li>
  </ol>
);

const TIKTOK_FIELDS = [
  { key: 'access_token', label: 'Access Token', type: 'password', secret: true, placeholder: 'Pega el access token de TikTok (long-lived)', hint: 'No expira salvo revocación (igual que el System User de Meta).' },
  { key: 'advertiser_id', label: 'Advertiser ID', type: 'text', placeholder: '7000000000000000000', hint: 'Cuenta publicitaria de TikTok Ads Manager → campañas PAGADAS.' },
  { key: 'business_id', label: 'Business ID (orgánico, opcional)', type: 'text', placeholder: '7000000000000000000', hint: 'Cuenta TikTok Business → estadísticas ORGÁNICAS (perfil, videos). Requiere scopes de cuenta en el token.' },
];

const TIKTOK_HELP = (
  <ol>
    <li>Entra a <strong>TikTok for Business → Developers</strong> (business-api.tiktok.com) y crea una <strong>App</strong>.</li>
    <li>Habilita los productos que uses: <em>Ads Management / Reporting</em> (pagado) y, para lo orgánico, <em>Accounts API</em> (con scopes de cuenta y video). Anota <strong>App ID</strong> y <strong>Secret</strong> y pon tu redirect URL.</li>
    <li>Autoriza la app con tu cuenta de <strong>TikTok Ads Manager</strong>; TikTok te redirige con un <strong>auth_code</strong>.</li>
    <li>Canjea el auth_code por el <strong>Access Token</strong> long-lived (<code>oauth2/access_token</code>) y pégalo aquí con el <strong>Advertiser ID</strong> (pagado) y/o el <strong>Business ID</strong> (orgánico).</li>
  </ol>
);

// embedded: se pinta como pestaña del Panel de Administración (/admin?tab=integrations),
// sin su propio header ni scroll de página.
export default function IntegrationsPage({ embedded = false }) {
  const { user } = useAuthenticator((context) => [context.user]);
  const username = user?.username;

  return (
    <main className={`int-page ${embedded ? 'embedded' : ''}`}>
      {!embedded && (
      <header className="int-header">
        <span className="int-header-icon"><FaPlug /></span>
        <div>
          <h1 className="int-title">Integraciones</h1>
          <p className="int-subtitle">Conecta las APIs oficiales que alimentan los tableros. Solo administradores. Las credenciales se guardan cifradas y nunca se muestran de vuelta.</p>
        </div>
      </header>
      )}

      <div className="int-grid">
        <IntegrationCard
          provider="meta"
          title="Meta"
          subtitle="Campañas (Marketing API) + Orgánico (Graph API)"
          icon={<FaFacebook />}
          accent="#0866FF"
          fields={META_FIELDS}
          api={metaCardApi}
          username={username}
        />

        <IntegrationCard
          provider="google_analytics"
          title="Google Analytics 4"
          subtitle="Tráfico web (Data API) · Service Account"
          icon={<FaChartLine />}
          accent="#E8710A"
          fields={GA_FIELDS}
          api={googleApi('google_analytics')}
          help={GA_HELP}
          username={username}
        />

        <IntegrationCard
          provider="google_ads"
          title="Google Ads"
          subtitle="Campañas pagadas de Google · OAuth + developer token"
          icon={<FaBullhorn />}
          accent="#3C8BD9"
          fields={GADS_FIELDS}
          api={googleApi('google_ads')}
          help={GADS_HELP}
          username={username}
        />

        <IntegrationCard
          provider="tiktok_ads"
          title="TikTok Ads"
          subtitle="Campañas pagadas de TikTok · Business/Marketing API"
          icon={<FaTiktok />}
          accent="#FE2C55"
          fields={TIKTOK_FIELDS}
          api={googleApi('tiktok_ads')}
          help={TIKTOK_HELP}
          username={username}
        />
      </div>

      {/* Tokens adicionales de Meta: agencias que pautan desde OTRO portafolio
          de negocio, cuyo System User no alcanza el token principal. */}
      <MetaExtraCredentials username={username} />

      <p className="int-foot"><FaGoogle /> Todas las llamadas a las APIs ocurren en el backend; los tokens y llaves jamás viajan al navegador.</p>
    </main>
  );
}
